import request from './request';

/**
 * Gallery API – single store (Kamil Store). No shopId or store filter; all media is global.
 */

/** Core store folders only (marketing, catalog, promos, users, CS, repairs). */
export const GALLERY_CORE_FOLDER_SLUGS = [
  'banners',
  'stories',
  'blogs',
  'categories',
  'products',
  'popups',
  'stocks',
  'extras',
  'coupons',
  'discounts',
  'users',
  'order_refunds',
  'receipts',
  'reviews',
  'repairs',
];

export function isGalleryCoreFolder(slug) {
  const s = String(slug || '').toLowerCase();
  return GALLERY_CORE_FOLDER_SLUGS.includes(s);
}

/**
 * Normalize FormData for new gallery upload API: ensure folderName is set
 * (backend expects folderName; existing callers send type).
 */
function normalizeUploadFormData(formData) {
  if (typeof FormData === 'undefined' || !(formData instanceof FormData)) return formData;
  if (!formData.get('folderName') && formData.get('type')) {
    formData.append('folderName', formData.get('type'));
  }
  return formData;
}

const galleryService = {
  /** GET /gallery/all – folders with optional first page of files per folder */
  getAllFolders: (params = {}) =>
    request.get('dashboard/gallery/all', {
      params: {
        includeFiles: params.includeFiles ?? false,
        perPage: params.perPage ?? 10,
        ...params,
      },
    }),

  /** POST /gallery/upload – multipart: folderName + file | image | files */
  uploadToGallery: (formData) => {
    normalizeUploadFormData(formData);
    const config =
      typeof FormData !== 'undefined' && formData instanceof FormData
        ? { headers: { 'Content-Type': undefined } }
        : {};
    return request.post('dashboard/gallery/upload', formData, config);
  },

  /** DELETE /gallery/delete – single file by file_url */
  deleteFile: (fileUrl) =>
    request.delete('dashboard/gallery/delete', { data: { file_url: fileUrl } }),

  /** DELETE /gallery/delete – all files in folder (do not use for receipts/order_refunds) */
  deleteFolder: (folderName) =>
    request.delete('dashboard/gallery/delete', { data: { folder_name: folderName } }),

  /** List files in one folder (pagination). GET galleries/storage/files */
  getAll: (params) =>
    request.get('dashboard/galleries/storage/files', { params }),

  /** Every file, unfiltered — page-through-all-folders view for the picker. */
  paginateAll: (params = {}) =>
    request.get('dashboard/galleries/paginate', { params }),

  /** Unused-file audit (backend scans every image URL across every model). */
  listUnused: (params = {}) =>
    request.get('dashboard/admin/media/unused', { params }),

  /** Delete every unused file the audit currently sees. */
  deleteAllUnused: () => request.delete('dashboard/admin/media/unused'),

  /** Backward compatibility: upload via new endpoint */
  upload: (data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return galleryService.uploadToGallery(data);
    }
    return request.post('dashboard/galleries', data);
  },

  /** Legacy delete (ids[]). Prefer deleteFile/deleteFolder for new code. */
  delete: (params) =>
    request.post('dashboard/galleries/storage/files/delete', {}, { params }),

  /** Folder slugs for media picker – core store folders only, stable order */
  getFolders: (params = {}) =>
    galleryService.getAllFolders({ ...params, includeFiles: false }).then((res) => {
      const folders = res?.data?.folders ?? [];
      const norm = (f) =>
        String(f.slug ?? f.name ?? '')
          .toLowerCase()
          .replace(/-/g, '_')
          .trim();
      const bySlug = new Map(folders.map((f) => [norm(f), f.slug ?? f.name]));
      const slugs = GALLERY_CORE_FOLDER_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
      return { ...res, data: slugs };
    }),

  storeMany: (data) => {
    const config =
      typeof FormData !== 'undefined' && data instanceof FormData
        ? { headers: { 'Content-Type': undefined } }
        : {};
    return request.post('dashboard/galleries/store-many', data, config);
  },
};

export default galleryService;
